import type {IContrato} from "@models/contratos/contrato.js";
import {
	Chip,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	Paper,
	Tooltip,
	Switch,
	TableCell,
	TableRow,
	TextField,
	Typography
} from "@mui/material";
import {ToMoney} from "@shared/MathHelp.js";
import {DefaultEventProps, DefaultInformationProps} from "@models/DefaultProps.ts";
import React, {useEffect, useState} from "react";
import {OpenInNew} from "@mui/icons-material";
import {createSearchParams, useNavigate} from "react-router";
import {MoneyInputEntry} from "@shared/specialComponents/InputEntry.tsx";
import {FornecedorPesquisa} from "@components/fornecedores/FornecedorPesquisa.tsx";
import {fullWidth} from "@models/helpers.ts";
import {FornecedorInformacaoFromId} from "@components/fornecedores/FornecedorInformacao.tsx";
import {DateField} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {useGeneral} from "@contexts/GeneralContext.tsx";
import {ContratoInfoNav} from "@components/contratos/ContratoInfoNav.tsx";
import {useContrato} from "@components/contratos/hooks/useContrato.tsx";
import {IContratoDetalhes} from "@components/contratos/models/IContratoDetalhes.ts";

interface ContratoInformacaoProps extends DefaultInformationProps<IContrato>, Pick<DefaultEventProps<IContrato>, 'onChange'> {
	value: IContrato;
	variant: 'grid' | 'list' | 'navigationLink' | 'row';
	removeOpenButton?: boolean;
	readonly?: boolean;
}


export const ContratoInformacao: React.FC<ContratoInformacaoProps> = (props) => {
	const {variant} = props;

	if (variant === 'grid') {
		return <ContratoInfoGrid {...props}/>;
	}
	if (variant === 'list') {
		return <ContratoInfoList {...props}/>;
	}
	if (variant === 'navigationLink') {
		return <ContratoInfoNav {...props}/>;
	}
	if (variant === 'row') {
		return <ContratoInfoRow {...props}/>;
	}

	return (
		<div>
			<h1>Contrato Informações</h1>
			<p>Informações detalhadas sobre o contrato selecionado.</p>
		</div>
	);
};

export function ContratoInfoGrid(props: ContratoInformacaoProps) {
	const {value} = props;
	const {adjustSizeLevel} = useGeneral()
	const {getContratoDetalhesById, useContratoValue} = useContrato()

	const [contrato, setContrato] = useContratoValue(value);
	const [detalhes, setDetalhes] = useState<IContratoDetalhes>();

	useEffect(() => {
		if (contrato == value) return;

		if (props.onChange) props.onChange?.(contrato);
	}, [contrato]);

	useEffect(() => {
		getContratoDetalhesById({idContrato: value.id})
			.then(x => {
				if (x.items != null) {
					setDetalhes(x.items)
				}
			})
	}, [value.id]);

	const handleDiferimentoChange = (check: boolean) => {
		if (contrato == null) return
		let novoValor = contrato.valor;

		if (check && (novoValor === 0 || novoValor == detalhes?.valorTotalParcelas) && detalhes?.valorTotalParcelas) {
			novoValor = detalhes.valorTotalParcelas;
		}

		setContrato({
			...contrato,
			isUsaDiferimento: check,
			valor: novoValor,
		});
	};

	return (
		contrato != null &&
		<Grid container spacing={1.5}>
			<Grid size={12}>
				<Grid container>
					<Grid>
						<TextField fullWidth label={'Id'} disabled value={value.id}/>
					</Grid>
					<Grid size={'grow'}>
						<TextField fullWidth label={'Nome'}
						           value={contrato.nome}
						           disabled={!contrato.isDraft || props.readonly}
						           required
						           focused={contrato.nome != value.nome}
						           onChange={(v) => {
							           setContrato({
								           ...contrato,
								           nome: v.target.value,
							           });
						           }}/>
					</Grid>
				</Grid>
			</Grid>
			<Grid size={12}>
				<TextField fullWidth
				           multiline
				           maxRows={3}
				           label={'Descricao'}
				           value={contrato.descricao}
				           disabled={!contrato.isDraft || props.readonly}
				           focused={props.value.descricao != contrato.descricao}
				           onChange={(v) => {
					           setContrato({
						           ...contrato,
						           descricao: v.target.value,
					           });
				           }}/>
			</Grid>
			<Grid size={12}>
				<Grid container>
					<Grid size={'grow'}>
						<FornecedorPesquisa options={{initialValue: contrato.fornecedor}}
						                    required
						                    disabled={!contrato.isDraft || props.readonly}
						                    onItemSelected={(f) => {
							                    setContrato({
								                    ...contrato,
								                    fornecedorId: f?.id ?? value.fornecedorId,
								                    fornecedor: f
							                    });
						                    }}
						/>
					</Grid>
					<Grid>
						<MoneyInputEntry label={"Valor"}
						                 disabled={!contrato.isDraft || props.readonly}
						                 onChange={(e) => {
							                 setContrato({
								                 ...contrato,
								                 valor: e || 0,
							                 });
						                 }}
						                 value={contrato.valor}/>
					</Grid>
				</Grid>
			</Grid>
			<Grid size={12}>
				<Grid container>
					<Grid size={adjustSizeLevel({
						lg: 6,
						sm: 12
					})}>
						<DateField fullWidth
						           label={'Data de inicio'}
						           value={dayjs(contrato.dataInicio)}/>
					</Grid>
					<Grid size={adjustSizeLevel({
						lg: 6,
						sm: 12
					})}>
						<DateField fullWidth
						           label={'Data final'}
						           value={contrato.isTerminoIndefinido ? null : dayjs(contrato.dataFim)}/>
					</Grid>
				</Grid>
			</Grid>
			<Grid size={12}>
				<Divider/>
				<Grid container px={1.5} spacing={1.5} p={1}>
					<Grid size={adjustSizeLevel({
						md: 6,
						sm: 12
					})}>
						<FormControl>
							<FormControlLabel checked={contrato.isUsaDiferimento}
							                  disabled={!contrato.isDraft || props.readonly}
							                  onChange={(_e, v) => handleDiferimentoChange(v)}
							                  label={<Tooltip
								                  title={'Quando marcado, o valor informado no contrato será consumido do recurso ao ativar'}>
								                  <span>Aplica diferimento</span>
							                  </Tooltip>}
							                  control={<Switch/>}/>
						</FormControl>
					</Grid>
					<Grid size={adjustSizeLevel({
						md: 6,
						sm: 12
					})}>
						<FormControl>
							<FormControlLabel checked={contrato.isTerminoIndefinido}
							                  disabled={!contrato.isDraft || props.readonly}
							                  onChange={() => {
								                  setContrato({
									                  ...contrato, isTerminoIndefinido: !contrato.isTerminoIndefinido
								                  })
							                  }}
							                  label={'Termino indefinido'} control={<Switch/>}/>
						</FormControl>
					</Grid>
				</Grid>
				<Divider/>
			</Grid>
			<Grid size={12}>
				{detalhes && (<Grid container rowSpacing={1.5}>
					<Grid size={adjustSizeLevel({
						md: 6,
						sm: 12
					})}>
						<TextField label={'Valor total das parcelas'}
						           value={ToMoney(detalhes.valorTotalParcelas)}
						           disabled/>
					</Grid>
					<Grid size={adjustSizeLevel({
						md: 6,
						sm: 12
					})}>
						<TextField label={'Parcelas'} value={detalhes.qtdParcelas} disabled/>
					</Grid>
					<Grid size={adjustSizeLevel({
						md: 6,
						sm: 12
					})}>
						<DateField fullWidth
						           variant={'filled'}
						           label={'Primeira parcela'} value={dayjs(detalhes.primeiraParcela)}
						           disabled/>
					</Grid>
					<Grid size={adjustSizeLevel({
						md: 6,
						sm: 12
					})}>
						<DateField fullWidth
						           variant={'filled'}
						           label={'Ultima parcela'}
						           value={dayjs(detalhes.ultimaParcela)}
						           disabled/>
					</Grid>
				</Grid>)}
			</Grid>
		</Grid>
	)
}

const ContratoInfoList: React.FC<ContratoInformacaoProps> = (props) => {
	const {value} = props;
	const navigate = useNavigate();

	return (
		<Grid container component={Paper} elevation={1} p={1} spacing={1}>
			<Grid size={12}>
				<Grid container justifyContent={'space-between'}>
					<Grid container spacing={1} alignContent={'center'} justifyContent={'space-between'}>
						<Grid container spacing={1}>
							<Grid>
								<ContratoInfoNav {...props}/>
							</Grid>
							<Grid>
								<Typography>{value.descricao}</Typography>
							</Grid>
							{value.isDraft &&
								<Grid><Chip variant={'outlined'} color={"info"} size={'small'} label={'DRAFT'}/></Grid>}
						</Grid>
					</Grid>
					{!props.removeOpenButton && <Grid>
						<IconButton size={"small"}
						            onClick={() => {
							            navigate({
								            pathname: `/contratos/detalhes`,
								            search: createSearchParams({contratoId: value.id.toString()}).toString(),
							            });
						            }}
						>
							<OpenInNew/>
						</IconButton>
					</Grid>}
				</Grid>
			</Grid>
		</Grid>
	);
};

export function ContratoInfoRow(props: Omit<ContratoInformacaoProps, 'variant' | 'removeOpenButton' | 'readonly'>) {
	return (
		<TableRow>
			<TableCell>
				<ContratoInfoNav tableMode displayMode={'id'} {...props}/>
			</TableCell>
			<TableCell>
				<ContratoInfoNav tableMode {...props}/>
			</TableCell>
			<TableCell>
				<Typography>{props.value.descricao}</Typography>
			</TableCell>
			<TableCell>
				<Typography>{ToMoney(props.value.valor)}</Typography>
			</TableCell>
			<TableCell>
				{props.value.fornecedorId &&
					<FornecedorInformacaoFromId value={props.value.fornecedorId}
					                            variant={'navigationLink'}
					                            slotProps={{chip: {tableMode: true}}}/>}
			</TableCell>
			<TableCell>
				{props.value.isDraft &&
					<Chip variant={'outlined'} color={"info"} size={'small'}
					      label={'EM DRAFT'}
					      sx={{...fullWidth}}/>}
			</TableCell>
		</TableRow>
	)
}